"use client";

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSOLAFloating } from "./context";
import { ChatInterface as MainChatInterface } from "@/components/sola";
import { useSolaContext } from "@/hooks/use-sola-context";
import { useAxios } from "@/app/providers/AxiosProvider";

export function ChatInterface({ onNewChatReady }) {
  const { state, setTransport } = useSOLAFloating();
  const [recruiter, setRecruiter] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const api = useAxios();

  // Initialize SOLA context
  const {
    context,
    updateCurrentCandidate,
    visibilityType,
  } = useSolaContext({
    initialContext: {},
    initialVisibility: "private",
  });

  // Load current candidate when viewing a candidate page
  useEffect(() => {
    const loadCurrentCandidate = async () => {
      if (state.pageContext.pageType === 'candidate' && state.pageContext.entityId) {
        try {
          // Note: Backend uses /candidate (singular) with query param
          const response = await api.get("/candidate", {
            params: {
              uuid: state.pageContext.entityId,
            },
          });
          const candidateData = response.data;
          updateCurrentCandidate(candidateData);
        } catch (error) {
          console.error("[SOLA Floating Chat] Failed to fetch current candidate:", error);
          updateCurrentCandidate(null);
        }
      } else {
        // Clear current candidate when not on a candidate page
        updateCurrentCandidate(null);
      }
    };

    loadCurrentCandidate();
  }, [state.pageContext.pageType, state.pageContext.entityId, api, updateCurrentCandidate]);

  // Load recruiter data - REQUIRED for Cube query filtering
  useEffect(() => {
    const loadRecruiter = async () => {
      try {
        const response = await api.get('/me');
        const recruiterData = response.data;
        setRecruiter(recruiterData);
      } catch (error) {
        console.error("[SOLA Floating Chat] Failed to fetch recruiter:", error);
        setRecruiter(null);
      }
    };

    loadRecruiter();
  }, [api]);

  // Merge floating context with recruiter data
  // Memoized to prevent unnecessary re-renders from unstable object references
  const enhancedContext = useMemo(() => {
    return {
      ...context,
      recruiter: recruiter, // CRITICAL: Required for Cube query filtering
      currentPage: state.currentPage,
      pageContext: state.pageContext,
      selectedElements: state.selectedElements,
      preferences: state.preferences,
      pageType: state.pageContext.pageType,
      entityId: state.pageContext.entityId,
      route: state.pageContext.route,
    };
  }, [context, recruiter, state.currentPage, state.pageContext, state.selectedElements, state.preferences]);

  // Stable callback to avoid re-renders
  const handleTransportReady = useCallback((transport) => {
    setTransport(transport);
  }, [setTransport]);

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <MainChatInterface
        context={enhancedContext}
        visibilityType={visibilityType}
        onTransportReady={handleTransportReady}
        onNewChatReady={onNewChatReady}
        pageContext={state.pageContext}
      />
    </div>
  );
}
